import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Divider, Grid } from "@mui/material";
import PropTypes from "prop-types";

import MegaMenuItemPresenter from "presenters/MegaMenuItemPresenter";

import LinkList from "./components/LinkList";
import useStyles from "./useStyles";

const LinkListContainer = React.memo(({ links, mainMenuLinks, onLinkActiveChange }) => {
  const classes = useStyles();
  const [activeLists, setActiveLists] = useState(new Set());

  const LIST_SIZE = 8;

  const lists = useMemo(() => {
    if (links.length === 0) return [];

    const newList = [];
    for (let i = 0; i < links.length; i += LIST_SIZE) {
      newList.push(links.slice(i, i + LIST_SIZE));
    }
    return newList;
  }, [links, LIST_SIZE]);

  const handleListActiveChange = useCallback((listIndex, isActive) => {
    setActiveLists((prev) => {
      const newSet = new Set(prev);
      isActive ? newSet.add(listIndex) : newSet.delete(listIndex);
      return newSet;
    });
  }, []);

  const hasAnyActiveLinks = useMemo(() => activeLists.size > 0, [activeLists.size]);

  useEffect(() => {
    if (onLinkActiveChange) onLinkActiveChange(hasAnyActiveLinks);
  }, [hasAnyActiveLinks, onLinkActiveChange]);

  const listItems = useMemo(
    () =>
      lists.map((list, index) => (
        <React.Fragment key={index}>
          <LinkList
            list={list}
            mainMenuLinks={mainMenuLinks}
            onLinkActiveChange={(isActive) => handleListActiveChange(index, isActive)}
          />
          {index !== lists.length - 1 && <Divider className={classes.divider} orientation="vertical" />}
        </React.Fragment>
      )),
    [lists, mainMenuLinks, handleListActiveChange, classes.divider],
  );

  return (
    <Grid container>
      <Grid>
        <Grid className={classes.listsContainer}>{listItems}</Grid>
      </Grid>
    </Grid>
  );
});

LinkListContainer.displayName = "LinkListContainer";

LinkListContainer.propTypes = {
  links: PropTypes.arrayOf(MegaMenuItemPresenter.shape()),
  mainMenuLinks: PropTypes.arrayOf(PropTypes.string),
  onLinkActiveChange: PropTypes.func,
};

export default LinkListContainer;
